import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const AUTOPLAY_MS = 6000;

const getMobileObjectPosition = (slide = {}) => {
  if (slide.mobilePosition) return slide.mobilePosition;
  const src = String(slide.image || '');
  if (src.includes('classroom-wide')) return '62% center';
  if (src.includes('morse-kit')) return '50% center';
  if (src.includes('competition-awards')) return '56% center';
  return slide.objectPosition || 'center center';
};
// 鍏ㄥ睆鍥剧墖杞挱锛氱函灞曠ず锛岀偣鍑绘暣寮犲浘璺宠浆鍒板搴旈摼鎺ワ紙濡傛柊闂昏鎯咃級
export default function HeroCarousel({ slides = [] }) {
  const [index, setIndex] = useState(0);
  const [imgOk, setImgOk] = useState({});
  const pausedRef = useRef(false);
  const touchX = useRef(null);
  const swiped = useRef(false);
  const nav = useNavigate();

  const count = slides.length;
  useEffect(() => { setIndex(0); }, [count]);

  const go = useCallback((i) => setIndex((i + count) % count), [count]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    if (count <= 1) return undefined;
    const t = setInterval(() => { if (!pausedRef.current) setIndex((p) => (p + 1) % count); }, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [count]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; swiped.current = false; };
  const onTouchMove = (e) => {
    if (touchX.current != null && Math.abs(e.touches[0].clientX - touchX.current) > 10) swiped.current = true;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  const onSlideClick = (e, link) => {
    if (swiped.current) { e.preventDefault(); return; } // 滑动中不触发跳转
    if (link) nav(link);
  };

  const onActionClick = (e, link) => {
    e.stopPropagation();
    if (link) nav(link);
  };

  if (!count) return <div className="h-screen" />;

  return (
    <section
      className="site-hero relative h-[100svh] min-h-[590px] w-full overflow-hidden bg-slate-900 gpu-layer sm:h-screen"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
    >
      {/* 杞ㄩ亾 */}
      <div
        className="flex h-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((s, i) => {
          const [g1, g2] = s.gradient || ['#1e3a8a', '#2563eb'];
          const link = s.link || s.ctaLink || '';
          const objectPosition = s.objectPosition || 'center center';
          const mobileObjectPosition = getMobileObjectPosition(s);
          return (
            <div
              key={s.id}
              onClick={(e) => onSlideClick(e, link)}
              className={`relative h-full w-full shrink-0 ${link ? 'cursor-pointer' : ''}`}
            >
              <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})` }} />
              {s.image && (
                <img
                  src={s.image}
                  alt={s.eyebrow || s.title || '展示图片'}
                  draggable="false"
                  className={`hero-image absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${imgOk[s.id] ? 'opacity-100' : 'opacity-0'} ${i === index ? 'kenburns' : ''}`}
                  style={{ '--hero-pos-desktop': objectPosition, '--hero-pos-mobile': mobileObjectPosition }}
                  onLoad={() => setImgOk((m) => ({ ...m, [s.id]: true }))}
                  onError={() => setImgOk((m) => ({ ...m, [s.id]: false }))}
                />
              )}
              <div className="absolute inset-0 hero-scrim" />
              <div className="container-x pointer-events-none absolute inset-x-0 top-0 z-10 flex h-full items-center">
                <div className="hero-copy max-w-3xl">
                  {s.eyebrow && <div className="hero-kicker">{s.eyebrow}</div>}
                  {s.title && (
                    <h1 className="hero-title">
                      {String(s.title).split('\n').map((line, lineIndex, lines) => (
                        <React.Fragment key={`${line}-${lineIndex}`}>
                          {line}
                          {lineIndex < lines.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </h1>
                  )}
                  {s.subtitle && <p className="hero-subtitle">{s.subtitle}</p>}
                  <div className="hero-actions pointer-events-auto mt-8 flex flex-wrap gap-3">
                    {s.ctaText && (
                      <button type="button" className="btn btn-hero" onClick={(e) => onActionClick(e, s.ctaLink || link)}>
                        {s.ctaText} <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                    {s.altText && (
                      <button type="button" className="btn btn-hero-ghost" onClick={(e) => onActionClick(e, s.altLink || link)}>
                        {s.altText}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {count > 1 && (
        <>
          <button onClick={prev} aria-label="Previous slide" className="absolute left-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur transition-colors hover:bg-black/50 cursor-pointer sm:flex">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button onClick={next} aria-label="Next slide" className="absolute right-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur transition-colors hover:bg-black/50 cursor-pointer sm:flex">
            <ChevronRight className="h-6 w-6" />
          </button>
          {/* 指示器用摩斯语法：当前页是"划"，其余是"点" */}
          <div className="absolute bottom-7 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3">
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
                className={`morse-indicator ${i === index ? 'is-dash' : ''}`}
              />
            ))}
          </div>
        </>
      )}

      {/* 向下滚动提示 */}
      <div className="pointer-events-none absolute bottom-16 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center gap-1.5 text-white/80 sm:flex">
        <span className="text-[12px] font-medium tracking-[0.3em]">向下滑动</span>
        <ChevronDown className="scroll-cue h-5 w-5" />
      </div>
    </section>
  );
}

